import { inputAdornmentClasses, InputBaseProps } from '@mui/material';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import { outlinedInputClasses, OutlinedInputProps } from '@mui/material/OutlinedInput';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { DatePicker, TimePicker } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { get } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { desktopActionBar } from '~components/FormFields/desktopActionBar';
import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';

const parseInitialValue = (
  initialValue: string | number | Dayjs | Date | null | undefined,
) => {
  if (!initialValue) {
    return null;
  }

  if (dayjs.isDayjs(initialValue) && initialValue.isValid()) {
    return initialValue;
  }

  try {
    const parsed = dayjs.tz(initialValue);

    if (parsed.isValid()) {
      return parsed;
    }

    return null;
  } catch {
    return null;
  }
};

interface SingleDateFormFieldProps {
  InputProps?: Partial<OutlinedInputProps>;
  clearOnNull?: boolean;
  control: Control<FieldValues>;
  dataUi?: string;
  dateLabel?: string;
  dense?: boolean;
  disabled?: boolean;
  errors: FieldErrors<FieldValues>;
  fullWidth?: boolean;
  id?: string;
  inputProps?: InputBaseProps['inputProps'];
  isRequired?: boolean;
  multiline?: boolean;
  name: string;
  onPartialValidityChange?: (isPartiallyValid: boolean) => void;
  placeholder?: string;
  rows?: number;
  sx?: SxProps<Theme>;
  timeLabel?: string;
  type?: string;
  views?: ('year' | 'month' | 'day' | 'hours' | 'minutes')[];
}

type DayjsTzInput = Parameters<typeof dayjs.tz>[0];

interface SingleDateTimeFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
> extends Omit<SingleDateFormFieldProps, 'control' | 'errors'> {
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
}

const SingleDateTimeFormField = <TFieldValues extends FieldValues = FieldValues>({
  clearOnNull = false,
  control,
  dateLabel,
  dense,
  disabled = false,
  errors,
  fullWidth = true,
  isRequired,
  name,
  onPartialValidityChange,
  placeholder,
  sx,
  timeLabel,
  dataUi,
}: SingleDateTimeFormFieldProps<TFieldValues>) => {
  const fieldPath = name as FieldPath<TFieldValues>;
  const form = useFormContext();
  const initialValue = parseInitialValue(form.getValues(name) as DayjsTzInput);

  const [selectedDate, setSelectedDate] = useState(initialValue);
  const [selectedTime, setSelectedTime] = useState(initialValue);
  const [isWatchUpdate, setIsWatchUpdate] = useState(false);
  const watchedDateTime = form.watch(name as FieldPath<FieldValues>);
  // Mount-time effect runs setValue to normalize a string default into a
  // Dayjs; that path must NOT mark dirty. The two onChange handlers flip
  // this ref to true so subsequent effect-driven setValue calls (i.e. the
  // genuine user edits) pass { shouldDirty: true } and v2's sparse PATCH
  // (deparseUpdateV2) actually ships effective_at / expires_at.
  const hasUserInteractedRef = useRef(false);

  useEffect(() => {
    onPartialValidityChange?.(false);

    // If the date is being updated by the watched variable, we don't need to update the date/time
    if (isWatchUpdate) {
      return;
    }

    // shouldDirty gate: mark dirty only after the user has interacted with
    // a picker (onChange flips hasUserInteractedRef). The legacy code never
    // passed shouldDirty:true, which was fine for v1's always-emit
    // deparseUpdate but broke v2's sparse-PATCH deparseUpdateV2 (dirtyFields
    // for effectiveAt/expiresAt stayed false → the BE never received them).
    const dirtyOpts = hasUserInteractedRef.current ? { shouldDirty: true } : undefined;

    if (clearOnNull) {
      if (!selectedDate && !selectedTime) {
        setSelectedDate(null);
        setSelectedTime(null);
        form.setValue(name as FieldPath<FieldValues>, undefined, dirtyOpts);
        return;
      }
    }

    if (
      !(selectedDate?.isValid() && selectedTime?.isValid() && selectedDate.year() > 2000)
    ) {
      const hasValidDate = Boolean(selectedDate?.isValid());
      const hasValidTime = Boolean(selectedTime?.isValid());

      if ((hasValidDate || hasValidTime) && hasValidDate !== hasValidTime) {
        onPartialValidityChange?.(true);
      }

      return;
    }

    const newValue = selectedDate
      .clone()
      .set('hour', selectedTime.hour())
      .set('minute', selectedTime.minute());

    form.setValue(name as FieldPath<FieldValues>, newValue, dirtyOpts);
  }, [selectedDate, selectedTime, onPartialValidityChange]);

  useEffect(() => {
    if (watchedDateTime && watchedDateTime.toString().length > 0) {
      setIsWatchUpdate(true);
      setSelectedDate(dayjs.tz(watchedDateTime as DayjsTzInput));
      setSelectedTime(dayjs.tz(watchedDateTime as DayjsTzInput));
    }
  }, [watchedDateTime]);

  return (
    <Controller
      name={fieldPath}
      control={control}
      render={({ field }) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: dense ? 0 : 2,
            width: '100%',
          }}
        >
          <FormGroup sx={{ width: '100%' }}>
            {dateLabel && (
              <FormFieldLabel label={`${dateLabel}${isRequired ? ' *' : ''}`} />
            )}
            <DatePicker
              {...field}
              enableAccessibleFieldDOMStructure={false}
              value={field.value ? dayjs.tz(field.value as DayjsTzInput) : selectedDate}
              closeOnSelect={false}
              slotProps={{
                actionBar: desktopActionBar([]),
                textField: {
                  fullWidth: fullWidth,
                  placeholder: placeholder ?? 'mm/dd/yyyy',
                  required: isRequired,
                  variant: 'outlined',
                  className: isRequired ? 'required' : '',
                  sx: {
                    ...sx,
                    [`& .${inputAdornmentClasses.root}`]: { ml: 0 },
                    ...(dense
                      ? {
                          [`&.required .${outlinedInputClasses.root}:not(.Mui-error) .${outlinedInputClasses.notchedOutline}`]:
                            {
                              borderRightWidth: 1,
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0,
                            },
                          [`&.required .${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline}`]:
                            {
                              borderRightWidth: 1,
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0,
                            },
                          [`&:not(.required) .${outlinedInputClasses.root}:not(.Mui-error) .${outlinedInputClasses.notchedOutline}`]:
                            {
                              borderRightWidth: 1,
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0,
                            },
                          [`&:not(.required) .${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline}`]:
                            {
                              borderRightWidth: 1,
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0,
                            },
                        }
                      : {}),
                  },
                  error: Boolean(get(errors, name)),
                  slotProps: {
                    htmlInput: {
                      sx: { fontSize: '12px', height: '23px' },
                      ...(dataUi && { 'data-ui': `${dataUi}__date` }),
                    },
                  },
                },
              }}
              disabled={disabled}
              onChange={(e) => {
                hasUserInteractedRef.current = true;
                setIsWatchUpdate(false);
                setSelectedDate(e);
              }}
            />
          </FormGroup>
          <FormGroup sx={{ width: '100%' }}>
            {timeLabel && (
              <FormFieldLabel label={`${timeLabel}${isRequired ? ' *' : ''}`} />
            )}
            <TimePicker
              {...field}
              enableAccessibleFieldDOMStructure={false}
              value={field.value ? dayjs.tz(field.value as DayjsTzInput) : selectedTime}
              closeOnSelect={false}
              slotProps={{
                actionBar: desktopActionBar(['accept']),
                textField: {
                  fullWidth: fullWidth,
                  required: isRequired,
                  variant: 'outlined',
                  className: isRequired ? 'required' : '',
                  sx: {
                    ...sx,
                    ...(dense
                      ? {
                          [`&.required .${outlinedInputClasses.root}:not(.Mui-error) .${outlinedInputClasses.notchedOutline}`]:
                            {
                              borderLeftWidth: 1,
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            },
                          [`&.required .${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline}`]:
                            {
                              borderLeftWidth: 1,
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            },
                          [`&:not(.required) .${outlinedInputClasses.root}:not(.Mui-error) .${outlinedInputClasses.notchedOutline}`]:
                            {
                              borderLeftWidth: 1,
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                              borderLeftColor: 'transparent',
                            },
                          [`&:not(.required) .${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline}`]:
                            {
                              borderLeftWidth: 1,
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                              borderLeftColor: 'transparent',
                            },
                        }
                      : {}),
                  },
                  error: Boolean(get(errors, name)),
                  slotProps: {
                    htmlInput: {
                      style: { fontSize: '12px', height: '23px' },
                      ...(dataUi && { 'data-ui': `${dataUi}__time` }),
                    },
                  },
                },
              }}
              onChange={(e) => {
                hasUserInteractedRef.current = true;
                setIsWatchUpdate(false);
                setSelectedTime(e);
              }}
              disabled={disabled}
            />
          </FormGroup>
        </Box>
      )}
    />
  );
};

export { SingleDateTimeFormField };
